import api from './api';
import { handleApiError, PSErrorClass } from './apiError';
// The same entitlement block `/me` nests, so it's the same type and the same
// mapper — one place for the statuses, the renews/expires pair, and the perk
// labels this layer stands in for.
import type { MembershipBody, UserMembership } from './authApi';
import { toUserMembership } from './authApi';
import { PSPrice } from './eventsApi';

/**
 * ── membershipsApi — the membership-plan endpoints ──────────────────────
 *
 * Same contract as [[eventsApi]]: every type here is camelCase because [[api]]
 * converts the whole wire both directions, and a type is a CLAIM about the wire
 * that nothing validates.
 * ───────────────────────────────────────────────────────────────────────
 */

// ── GET /membership-plans ────────────────────────────────────────────────

/**
 * The cursor envelope the catalog arrives in — the same shape
 * `/me/reservations` uses, `{data, page:{cursor, nextCursor, hasMore}}`.
 *
 * `page` is optional for the same reason it is there: a block the server stopped
 * sending must not crash the list.
 */
type MembershipPlansBody = {
  data: Membership[];
  page?: {
    cursor: string | null;
    nextCursor: string | null;
    hasMore: boolean;
  };
};

/**
 * The whole plan catalog, newest plan first. **Public** — it works signed out,
 * and only inherits the token so nothing 401s mid-flow.
 *
 * This is the ONLY way to read a plan: there is no `GET /membership-plans/{id}`,
 * so whatever a detail screen shows has to be found in this list. Only
 * `is_active: true` plans come back, which makes a deactivated plan
 * indistinguishable from one that never existed — including the plan a user is
 * currently subscribed to.
 *
 * ── It does not page ────────────────────────────────────────────────────
 * 30 per page against a catalog of three, so only the first page is read and
 * `nextCursor` is dropped — the same call this file's sibling
 * [[listReservations]] makes, for the same reason. It IS a cap though, so it
 * says so out loud rather than silently truncating; the day that line appears,
 * the fix is to thread `cursor` through here.
 *
 * ── The plans it returns are not all buyable ────────────────────────────
 * A plan can be checked out only if it is `is_recurring` AND has a non-null
 * `gateway_plan_id` — both, or `POST /membership` is a 400. Nothing in the
 * response says so, and today two of staging's three plans fail the test
 * (Access Pass Premium has no Razorpay plan; the Trial Pass is one-time, which
 * the purchase endpoint has no path for). Neither field is on `Membership`
 * below, so this function can't tell a screen which button works — see the note
 * on the type.
 */
async function getAllMemberships(): Promise<Membership[]> {
  const response = await api.get<MembershipPlansBody>('/membership-plans');

  if (!response.ok || !response.data) handleApiError(response);

  const { data, page } = response.data;

  if (page?.hasMore) {
    // Not an error — see the docstring. Loud on purpose: this is the only sign
    // that the screen is showing a prefix of the catalog.
  }

  return data;
}

// ── POST /membership ─────────────────────────────────────────────────────

/**
 * The Razorpay Checkout arguments a new membership comes with — the server
 * created the order (or the subscription) and this describes it, ready for the
 * SDK bar the casing this layer restores at the call site.
 *
 * TWO shapes, and which one arrives is the plan's `isRecurring`:
 *
 *   - recurring → a SUBSCRIPTION. No `amount` and no `currency`: what the sheet
 *     charges today (a part-month addon) lives on the subscription at Razorpay,
 *     and what the user signs is a standing mandate rather than one payment.
 *     `shortUrl` is Razorpay's hosted page for the same mandate — unused, since
 *     we open the SDK.
 *   - one-time → an ORDER, the same three fields a booking's `payment` carries.
 *
 * Neither carries a merchant `name` or a `prefill`, unlike the booking flow's
 * block, so both of those are the caller's — see [[openMembershipSheet]].
 */
export type SubscriptionCheckout = {
  key: string;
  subscriptionId: string;
  shortUrl: string;
};

export type OrderCheckout = {
  key: string;
  orderId: string;
  /** PAISE, as a bare integer — what the SDK takes, not a money object. */
  amount: number;
  currency: string;
};

export type MembershipCheckout = SubscriptionCheckout | OrderCheckout;

/** What `POST /membership` gives back, narrowed to what the confirm step does with it. */
export type CreatedMembership = {
  id: string;
  /**
   * Null on the coupon path — a 100%-off membership arrives `active` with no
   * gateway subscription behind it, so there is nothing to pay and nothing to
   * open (see [[handleRedeem]]).
   */
  checkout: MembershipCheckout | null;
};

/**
 * Exactly one of `planId` / `couponCode`, which the union enforces: sending both
 * (or neither) is a 400 `invalid` from the server, and this way it can't compile.
 */
type CreateMembershipInput = { idempotencyKey: string } & (
  | { planId: string; couponCode?: never }
  | { couponCode: string; planId?: never }
);

/**
 * Buy a plan. 🔒 Authenticated, and the one WRITE in the flow: it validates the
 * plan, opens the order or subscription at Razorpay, and persists a `pending`
 * membership in one call.
 *
 * `Idempotency-Key` is REQUIRED here (unlike `POST /bookings`) — without it the
 * request 400s before the body is even read. Repeating a key replays the
 * original membership and the checkout payload it was issued with, which is what
 * makes pressing Pay again after dismissing the sheet resume the same purchase.
 *
 * Failures arrive as an [[ApiError]] whose `message` is the server's own words:
 * a 409 for a membership they already hold (`pending` counts, so an abandoned
 * checkout blocks the next attempt), a 400 `precondition_error` for an
 * incomplete profile, a 400 for a plan that isn't sellable.
 *
 * On the coupon path it also throws every code [[previewCoupon]] does — the
 * preview holds nothing, so the coupon's last redemption can go to someone else
 * in between (see [[isCouponCodeError]]).
 */
async function createMembership(
  input: CreateMembershipInput
): Promise<CreatedMembership> {
  const response = await api.post<CreateMembershipBody>(
    '/membership',
    // `plan` / `coupon_code` are the wire's spellings, and it is one body or the
    // other — the coupon names its own plan, so nothing else is sent with it.
    input.planId ? { plan: input.planId } : { couponCode: input.couponCode },
    { headers: { 'Idempotency-Key': input.idempotencyKey } }
  );

  if (!response.ok || !response.data) handleApiError(response);

  return {
    id: response.data.membership.id,
    checkout: response.data.checkout ?? null,
  };
}

// ── POST /membership/preview-coupon ──────────────────────────────────────

export type CouponPreview = {
  couponCode: string;
  /** The plan the coupon grants — NOT necessarily the one on screen. */
  plan: { id: string; name: string };
  /** ISO instant the granted membership would run until. */
  grantsUntil: string;
};

// The codes that are about the CODE the user typed, so a screen pins them to the
// input instead of toasting them. `conflict` is deliberately absent: a membership
// they already hold isn't a problem with the code.
const COUPON_CODE_ERROR_CODES = new Set([
  'not_found',
  'coupon_not_active',
  'coupon_expired',
  'invalid_coupon',
  'coupon_redemption_limit_reached',
  'coupon_already_used',
]);

export const isCouponCodeError = (error: PSErrorClass) =>
  error.kind === 'api' &&
  error.code !== null &&
  COUPON_CODE_ERROR_CODES.has(error.code);

/**
 * What a coupon would grant, without spending it. 🔒 Authenticated — one of the
 * failures is "you've already used this".
 *
 * Read-only, and a green preview is NOT a reservation: `POST /membership` throws
 * the SAME codes, so a coupon can lose the race to its own total cap between the
 * two calls and the submit path needs its own copy for all of them.
 */
async function previewCoupon(code: string): Promise<CouponPreview> {
  // Not deployed yet — [[couponPreviewMock]] owns the flag that picks a scenario.

  const response = await api.post<CouponPreview>('/membership/preview', {
    couponCode: code,
  });

  if (!response.ok || !response.data) handleApiError(response);

  return response.data;
}

// ── GET /me/memberships/{id} ─────────────────────────────────────────────

/**
 * One gateway charge attempt against a membership — a row of the ledger this
 * endpoint nests under `payments`, newest first.
 *
 * Narrowed to what a history row draws. The wire also sends `kind` (always
 * `membership` here), `booking` (null for that same reason), `membership` (the
 * parent's id, which the caller already has), `gatewayOrderId` /
 * `gatewayPaymentId`, and `createdAt` — the gateway's write time, a second or so
 * after `occurredAt`.
 *
 * `amount` is a MONEY OBJECT, not the bare paise integer this endpoint used to
 * send alongside a top-level `currency`: read `display` for the string,
 * `amountInMinorUnits` for arithmetic. It's the same [[PSPrice]] the plans
 * endpoint has always returned.
 */
export type MembershipPayment = {
  id: string;
  /**
   * `captured` — money moved. `failed` — a charge the gateway rejected, written
   * for the dunning events, and the only place a member can see why billing is
   * complaining. `created` — an order never taken further.
   */
  status: 'captured' | 'failed' | 'created';
  amount: PSPrice;
  /** `card`, `upi`… the gateway's own spelling, shown as-is. */
  method: string | null;
  /** When the charge happened — what the ledger is ordered by. */
  occurredAt: string;
};

/**
 * One membership in full: the summary block `/me` nests, plus its ledger. That
 * is the entire difference from [[UserMembership]] — one server-side helper
 * builds this, `/me`'s block and every row of `/me/memberships`, so the three
 * cannot drift.
 */
export type MembershipDetail = UserMembership & {
  /**
   * Newest first. EMPTY is normal and means several different things: a coupon
   * or grant membership was never charged, and a paid one activated by a forged
   * webhook has no charge behind it either. So an empty ledger on an `active`
   * paid membership is a fixture artefact, not a bug.
   */
  payments: MembershipPayment[];
  /**
   * The Razorpay arguments this purchase was opened with, re-readable for as
   * long as the membership is `pending` and null the moment it isn't. It is what
   * makes an abandoned checkout RESUMABLE: the mandate already exists at the
   * gateway, so a second `POST /membership` would authorize a second one rather
   * than finish this.
   *
   * The wire carries it on the shared block, so `/me` and every list row have it
   * too — it's modelled here because the manage screen is the only thing that
   * opens it.
   */
  checkoutPayload: MembershipCheckout | null;
};

// Both optional here but not on `MembershipDetail`: a server that stopped
// sending either key must leave the page drawing a membership with no ledger and
// no resume, rather than crashing the map below.
type MembershipDetailBody = MembershipBody & {
  payments?: MembershipPayment[];
  checkoutPayload?: MembershipCheckout | null;
};

/**
 * Read back one of the caller's own memberships. 🔒 Authenticated and scoped to
 * the caller, so someone else's id is a 404 — never a 403, and never a hint that
 * the id exists. A malformed uuid lands there too.
 *
 * Two callers, wanting different halves of it. The purchase poll
 * ([[usePollMembership]]) waits on `status`: `pending` is the only non-terminal
 * one, and it's where a membership sits until Razorpay's webhook reaches our
 * server. The manage screen wants `payments`, which is the only place a member
 * can see what they were actually charged — and, on a `pending` row,
 * `checkoutPayload`, which is the only place an abandoned checkout can be
 * reopened from.
 */
async function getMyMembership(id: string): Promise<MembershipDetail> {
  const response = await api.get<MembershipDetailBody>(`/me/memberships/${id}`);

  if (!response.ok || !response.data) handleApiError(response);

  return {
    ...toUserMembership(response.data),
    payments: response.data.payments ?? [],
    checkoutPayload: response.data.checkoutPayload ?? null,
  };
}

// ── DELETE /me/memberships/{id} ──────────────────────────────────────────

/**
 * Cancel a membership. 🔒 Authenticated, scoped to the caller. Replaces the old
 * `PATCH /membership/{id}/cancel`.
 *
 * 204 with NO body, so there is nothing to read back — refetch the membership
 * (or `/me`) to see the new state.
 *
 * ── The member keeps their access ───────────────────────────────────────
 * It flips to `cancelling`, and `cancelling` / `cancelled` are RESIDUAL: the
 * member paid for the current cycle, so entitlement runs to its end. `phase`
 * stays `current`, `renewsAt` goes null, and `expiresAt` / `daysRemaining` count
 * down to the boundary. At Razorpay the subscription is cancelled immediately,
 * so nothing further is charged — what remains is paid for, not a grace period.
 *
 * `cancelling` is not `cancelled`: the client asks, the `subscription.cancelled`
 * webhook confirms, and only then is `cancelledAt` set. Nothing sweeps a row
 * stuck in `cancelling`, which is harmless — it doesn't block a new purchase.
 *
 * Works on a coupon or grant membership too: there's no gateway subscription
 * behind one, so it's a local status flip. That used to be an unavoidable 503.
 *
 * Failures arrive as an [[ApiError]] with the server's own words: 409 `conflict`
 * for a membership already terminal (`cancelled` / `completed` / `expired` —
 * notably NOT `halted`), 503 `gateway_error` if Razorpay refused, 404 for an id
 * that isn't the caller's.
 *
 * ── One trap it leaves behind ───────────────────────────────────────────
 * `perksUsage` is built for status exactly `active`, so it goes null the moment
 * this returns while the perks still work. Treat null as "unknown" and gate on
 * `phase === 'current'`, never on the absence of that block.
 */
async function cancelMembership(id: string): Promise<void> {
  const response = await api.delete(`/me/memberships/${id}`);

  if (!response.ok) handleApiError(response);
}

// ── GET /me/memberships ──────────────────────────────────────────────────

/**
 * The cursor envelope the history arrives in — the same `{data, page}` shape
 * the plan catalog above uses, and optional for the same reason.
 */
type MyMembershipsBody = {
  data: MembershipBody[];
  page?: {
    cursor: string | null;
    nextCursor: string | null;
    hasMore: boolean;
  };
};

/**
 * Every membership this user has ever held, newest first. 🔒 Authenticated.
 *
 * One row per MEMBERSHIP, not per billing cycle — a six-month member is one row,
 * and per-cycle history isn't on the client API at all. Each row is the same
 * block `/me` nests, so `phase` (`current` / `upcoming` / `past`) is what groups
 * the list; at most one row reads `current`, and a `pending` purchase is a full
 * row at `upcoming` rather than being absent.
 *
 * Do NOT gate UI on this — `/me`'s `membership` is the entitlement. Two
 * consequences of the shared block worth knowing here: `perksUsage` is non-null
 * only on an `active` row and reflects usage NOW, not usage as it stood during a
 * membership that ended last year; and `cancelledAt` is OMITTED (not nulled) on
 * every row that isn't a `cancelled` one carrying a timestamp.
 */
async function listMyMemberships(): Promise<UserMembership[]> {
  const response = await api.get<MyMembershipsBody>('/me/memberships');

  if (!response.ok || !response.data) handleApiError(response);

  const { data, page } = response.data;

  if (page?.hasMore) {
    // Not an error, and the same unpaged cap the two lists above take — a user
    // holds a handful of passes, not a page's worth. Said out loud rather than
    // silently truncating; the fix is to thread `cursor` through here.
  }

  return data.map(toUserMembership);
}

// The create response. `membership` is a raw model dump of every column — among
// them `notes.first_cycle`, which holds the PRORATED amount the sheet charges
// today (the plan's price is what recurs from `charge_at`). Nothing renders that
// yet, so only the id is read.
type CreateMembershipBody = {
  membership: { id: string };
  checkout?: MembershipCheckout | null;
};

// ── The plan shape ───────────────────────────────────────────────────────
//
// Narrowed to what a plan card renders, this file's rule being eventsApi's: a
// field lands here when a screen draws it. The wire also sends
// `price`, `isTaxIncluded`, `gatewayPlanId`, `paymentGateway`, `features`
// (the raw m2m id list that `planFeatures` supersedes), and a row of vestigial
// columns (`enrolledCount` — a dead counter that reads 0 on a plan with members —
// `maxMembers`, `benefits`, `maxPreviewsPerMonth`, `discountType`,
// `discountValue`). Each is one line to add back; the transform already
// delivers it camelCased.
//
// `gatewayPlanId` is the one still worth knowing about: with `isRecurring` it's
// what says a plan can actually be BOUGHT (see `getAllMemberships`), and today
// the home upsell offers plans that fail the test. Add it when a Buy button has
// to decide.

export interface Feature {
  id: string;
  key: string;
  name: string;
  description: string;
}

/**
 * One perk on a plan. `feature.name` / `feature.description` are authored to be
 * shown as-is.
 *
 * `limit` reads backwards from how it looks: `null` is **unlimited**, and `0` is
 * **blocked** — the Trial Pass carries three zeroes, which is how a plan says
 * "this category is not included" and draws identically to an absent row unless
 * a card is written to tell them apart.
 *
 * The wire also sends `feature.isMetered` / `unit` (`"passes"`,
 * `"bookings/month"`) and `value.window` — `month` for a calendar month,
 * `membership` for the pass's lifetime. That window is what makes `limit: 2`
 * mean two a month or two ever, so it has to come back the moment a row prints
 * the number.
 */
export interface PlanFeature {
  id: string;
  feature: Feature;
  limit: number | null;
}

export interface Membership {
  id: string;
  /** The split behind `priceWithTax`; collapses to it when no tax profile applies. */
  basePrice: PSPrice;
  taxAmount: PSPrice;
  /**
   * The tax profile behind `taxAmount`, narrowed to the one thing a price row
   * draws: the RATE in its label ("Tax (18%)"). Null when no profile applies,
   * which is also when `taxAmount` reads zero.
   *
   * `totalPercentage` is a decimal STRING ("18.00"), like every other rate on
   * this API. The wire also sends `name` ("GST 18"), `isInclusive`, and a
   * CGST/SGST `breakdown` — none of which a row prints.
   */
  tax: { totalPercentage: string } | null;
  /**
   * The same three figures for THIS month only — a part-month charged today,
   * because cycles are calendar-month aligned and the mandate itself starts on
   * the 1st at the full price. They EQUAL the three above for a purchase on the
   * 1st, which is the only signal that nothing is prorated (see [[isProrated]]).
   * Null on a one-shot plan, which has no cycle to align.
   */
  proratedBasePrice: PSPrice | null;
  proratedTaxAmount: PSPrice | null;
  proratedPriceWithTax: PSPrice | null;
  /** What a full cycle costs — the figure to render. */
  priceWithTax: PSPrice;
  /** `access_pass` or `trial`. Stays snake_case: [[api]] converts keys, not values. */
  kind: 'trial' | 'access_pass';
  /**
   * The named access tier (`access_pass`, `premium`, `trial` on staging) — a free
   * CharField, and the ONLY id a plan shares with a membership a user holds. That
   * user-side `id` is the subscription row, not the plan, so this is what joins
   * the two (see [[deriveMembershipUpsell]]).
   */
  tier: string;
  name: string;
  description: string;
  /** `one_time` iff `!isRecurring` — the two always agree. */
  billingInterval: 'month' | 'quarter' | 'year' | 'one_time';
  /** False on the one-shot pass, which a user may buy exactly once. */
  isRecurring: boolean;
  /** The one-shot pass's lifespan. `null` on a recurring plan, which renews instead. */
  validityDays: number | null;
  planFeatures: PlanFeature[];
}

export default {
  getAllMemberships,
  createMembership,
  previewCoupon,
  getMyMembership,
  cancelMembership,
  listMyMemberships,
};
